import type { MouseEvent } from 'react';
import { forwardRef, useEffect, useMemo, useRef } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Loader } from '../../../../Elements/Loader/index.tsx';
import { Tooltip } from '../../../../Feedback/Tooltip/index.tsx';
import { ANIMATION_DURATION_MS } from '../../constants.ts';
import { useDropdownContext } from '../../context.tsx';
import { ClickableDropdownItemWrapper, LinkDropdownItemWrapper } from './styled.ts';
import type { DropdownItemProps } from './types.ts';

export const DropdownItem = forwardRef<HTMLDivElement, DropdownItemProps>(function DropdownItem(
  {
    children,
    id,
    icon,
    tooltip,
    wrap,
    isLoading,
    disabled,
    closeOnClick = true,
    last,
    onClick,
    to,
    dataTestId = 'dropdown-item',
    isSelected,
  },
  ref,
) {
  const { close } = useDropdownContext();
  const timeoutRef = useRef<number>();

  const handleClick = useCallbackRef((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!isLoading && !disabled && onClick) {
      if (closeOnClick) {
        close();
        timeoutRef.current = window.setTimeout(() => {
          onClick(e);
        }, ANIMATION_DURATION_MS);
      } else {
        onClick(e);
      }
    }
  });

  const containerProps = useMemo(
    () => ({
      id,
      wrap,
      disabled,
      $last: last,
      onClick: onClick ? handleClick : undefined,
      to,
      'data-testid': dataTestId,
      $isSelected: isSelected,
    }),
    [id, wrap, disabled, last, onClick, handleClick, to, dataTestId, isSelected],
  );

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return (
    <Tooltip
      tooltip={tooltip}
      placement='auto'
      ref={ref}
      ContainerComponent={to ? LinkDropdownItemWrapper : ClickableDropdownItemWrapper}
      containerComponentProps={containerProps}>
      {icon}
      {children}
      {isLoading && <Loader cover />}
    </Tooltip>
  );
});
